<script setup lang="ts">
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  EnvironmentOutlined,
  ExpandOutlined,
  HomeOutlined,
  PlusOutlined,
  RestOutlined,
} from '@ant-design/icons-vue'
import type { SelectProps } from 'ant-design-vue'
import { message } from 'ant-design-vue'
import { computed, h, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { Company, Property } from '@/models'
import { fetchCompanies } from '@/services/company'
import {
  createProperty,
  deleteProperty,
  fetchProperties,
  updateProperty,
} from '@/services/properties'
import { getDisplayUrl } from '@/services/storage'
import { useAuthStore } from '@/stores/auth'
import PropertyFormDialog from './properties/PropertyFormDialog.vue'

const router = useRouter()
const auth = useAuthStore()

const properties = ref<Property[]>([])
const companies = ref<Company[]>([])
const loading = ref(false)
const saving = ref(false)

const imageUrls = ref<Map<string, string>>(new Map())

function propertyImageUrl(property: Property): string {
  return imageUrls.value.get(property.id) || 'https://placehold.co/640x420?text=Property'
}
const dialog = ref(false)
const editingProperty = ref<Property | null>(null)

const search = ref<string>('')
const selectedType = ref<string>('All')
const selectedCompany = ref<string>('')
const sortField = ref<'title' | 'price' | 'beds' | 'baths' | 'sqm'>('title')
const sortDir = ref<'asc' | 'desc'>('asc')
const page = ref(1)
const pageSize = ref(12)

const propertyTypes = ref<SelectProps['options']>([
  { value: 'All', label: 'All' },
  { value: 'House', label: 'House' },
  { value: 'Apartment', label: 'Apartment' },
  { value: 'Condo', label: 'Condo' },
  { value: 'Villa', label: 'Villa' },
  { value: 'Townhouse', label: 'Townhouse' },
])

const companyNames = computed(() => {
  return new Map((companies.value || []).map((company) => [company.id, company.name]))
})

const companyOptions = computed<SelectProps['options']>(() => {
  return [
    { value: '', label: 'All Companies' },
    ...(companies.value || []).map((c) => ({ value: c.id, label: c.name })),
  ]
})

const filteredProperties = computed<Property[]>(() => {
  let result = properties.value.filter((property) => {
    const searchValue = search.value.toLowerCase()
    const matchesSearch =
      !searchValue ||
      property.title.toLowerCase().includes(searchValue) ||
      property.location.toLowerCase().includes(searchValue)
    const matchesType = selectedType.value === 'All' || property.type === selectedType.value
    const matchesCompany = !selectedCompany.value || property.company_id === selectedCompany.value
    return matchesSearch && matchesType && matchesCompany
  })

  result.sort((a, b) => {
    const aVal = (a as any)[sortField.value] ?? ''
    const bVal = (b as any)[sortField.value] ?? ''
    const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal))
    return sortDir.value === 'asc' ? cmp : -cmp
  })

  return result
})

const pagedProperties = computed(() => {
  const start = (page.value - 1) * pageSize.value
  return filteredProperties.value.slice(start, start + pageSize.value)
})

const multipleCompanies = computed(() => companies.value.length > 1)

const canAddProperty = computed(() => auth.can('properties', 'add'))

const loadData = async () => {
  loading.value = true
  try {
    const [propertiesRes, companiesRes] = await Promise.all([fetchProperties(), fetchCompanies()])
    properties.value = propertiesRes
    const urls = new Map<string, string>()
    await Promise.all(
      propertiesRes.map(async (p) => {
        urls.set(p.id, await getDisplayUrl(p.image))
      }),
    )
    imageUrls.value = urls
    companies.value = companiesRes
  } catch (error: any) {
    message.error(error?.message || 'Failed to load properties.')
  } finally {
    loading.value = false
  }
}

const openAddProperty = (): void => {
  if (!canAddProperty.value) {
    message.warning('You do not have permission to add properties.')
    return
  }

  editingProperty.value = null
  dialog.value = true
}

const canEditProperty = (property: Property): boolean =>
  auth.can('properties', 'edit', property.company_id)
const canDeleteProperty = (property: Property): boolean =>
  auth.can('properties', 'delete', property.company_id)

const viewPropertyDetails = (propertyId: string): void => {
  router.push(`/properties/${propertyId}`)
}

const editProperty = (property: Property, e: Event): void => {
  e.stopPropagation()
  if (!canEditProperty(property)) {
    message.warning('You do not have permission to edit this property.')
    return
  }
  editingProperty.value = { ...property }
  dialog.value = true
}

const removeProperty = async (property: Property) => {
  try {
    await deleteProperty(property.id)
    message.success('Property deleted successfully!')
    await loadData()
  } catch (error: any) {
    message.error(error?.message || 'Failed to delete property.')
  }
}

const handleSaveProperty = async (payload: Partial<Property>) => {
  saving.value = true
  try {
    if (editingProperty.value?.id) {
      await updateProperty(editingProperty.value.id, payload)
      message.success('Property updated successfully!')
    } else {
      await createProperty(payload)
      message.success('Property created successfully!')
    }

    dialog.value = false
    await loadData()
  } catch (error: any) {
    message.error(error?.message || 'Failed to save property.')
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  loadData()
})
</script>

<template>
  <a-flex style="margin-bottom: 16px" justify="space-between" gap="small" wrap="wrap" align="center">
    <a-space wrap>
      <a-input-search v-model:value="search" placeholder="Search properties..." />
      <a-select v-model:value="selectedType" style="width: 140px" :options="propertyTypes" />
      <a-select
        v-if="multipleCompanies"
        v-model:value="selectedCompany"
        show-search
        :filter-option="(input: string, option: any) => option.label.toLowerCase().includes(input.toLowerCase())"
        style="width: 200px"
        :options="companyOptions"
      />
      <a-select
        v-model:value="sortField"
        style="width: 130px"
        :options="[
          { value: 'title', label: 'Sort: Name' },
          { value: 'price', label: 'Sort: Price' },
          { value: 'beds', label: 'Sort: Beds' },
          { value: 'baths', label: 'Sort: Baths' },
          { value: 'sqm', label: 'Sort: Area' },
        ]"
      />
      <a-button @click="sortDir = sortDir === 'asc' ? 'desc' : 'asc'">
        <template #icon>
          <ArrowUpOutlined v-if="sortDir === 'asc'" />
          <ArrowDownOutlined v-else />
        </template>
      </a-button>
    </a-space>
    <a-space>
      <a-button @click="loadData" :loading="loading">Refresh</a-button>
      <a-button
        type="primary"
        :icon="h(PlusOutlined)"
        @click="openAddProperty"
        :disabled="!canAddProperty"
      >
        Add Property
      </a-button>
    </a-space>
  </a-flex>

  <a-alert
    v-if="!canAddProperty && !loading"
    type="info"
    show-icon
    message="Properties are managed by admins, owners, and managers assigned to companies."
    class="mb-4"
  />

  <a-spin :spinning="loading">
    <div
      v-if="filteredProperties.length === 0"
      style="display: flex; justify-content: center; align-items: center; min-height: 300px"
    >
      <a-empty description="No properties found" />
    </div>
    <a-flex v-else horizontal gap="middle" wrap="wrap">
      <a-card
        v-for="property in pagedProperties"
        :key="property.id"
        hoverable
        @click="viewPropertyDetails(property.id)"
        style="width: 320px"
      >
        <template #cover>
          <div style="position: relative">
            <a-image
              :src="propertyImageUrl(property)"
              :preview="false"
              style="height: 200px; object-fit: cover"
            />
            <a-tag
              v-if="property.featured"
              color="blue"
              style="position: absolute; top: 8px; left: 8px"
              >Featured</a-tag
            >
          </div>
        </template>

        <a-card-meta>
          <template #title>
            {{ property.title }}
          </template>
          <template #description>
            <div style="margin-bottom: 8px">
              <EnvironmentOutlined style="margin-right: 4px" />
              {{ property.location }}
            </div>
            <div style="margin-bottom: 8px">
              Company: {{ companyNames.get(property.company_id) || property.company_id }}
            </div>
            <a-row :gutter="8" style="margin-bottom: 8px">
              <a-col :span="8" class="text-center">
                <HomeOutlined class="mb-1" />
                <div>{{ property.beds }} Beds</div>
              </a-col>
              <a-col :span="8" class="text-center">
                <RestOutlined class="mb-1" />
                <div>{{ property.baths }} Baths</div>
              </a-col>
              <a-col :span="8" class="text-center">
                <ExpandOutlined class="mb-1" />
                <div>{{ property.sqm }} m²</div>
              </a-col>
            </a-row>
            <div class="text-xl font-bold text-blue-600 mt-2">
              ₱{{ Number(property.price || 0).toLocaleString('en-PH') }}
            </div>
          </template>
        </a-card-meta>

        <template #actions>
          <a-space align="end" style="width: 100%; justify-content: flex-end">
            <a-button
              @click.stop="editProperty(property, $event)"
              :disabled="!canEditProperty(property)"
              >Edit</a-button
            >
            <a-popconfirm
              :title="`Delete ${property.title}`"
              description="Are you sure you want to delete this property?"
              ok-text="Yes"
              cancel-text="No"
              @confirm="removeProperty(property)"
              :disabled="!canDeleteProperty(property)"
            >
              <a-button danger @click.stop :disabled="!canDeleteProperty(property)"
                >Delete</a-button
              >
            </a-popconfirm>
            <a-button type="primary" @click.stop="viewPropertyDetails(property.id)"
              >View Details</a-button
            >
          </a-space>
        </template>
      </a-card>
    </a-flex>
    <div v-if="filteredProperties.length > 0" style="display: flex; justify-content: center; margin-top: 16px">
      <a-pagination
        v-model:current="page"
        :page-size="pageSize"
        :total="filteredProperties.length"
        :show-total="(total: number) => `${total} properties`"
        show-size-changer
        :page-size-options="['12', '24', '48']"
        @show-size-change="(_cur: number, size: number) => { pageSize = size; page = 1 }"
      />
    </div>
  </a-spin>

  <!-- Property Form Dialog -->
  <PropertyFormDialog
    v-model="dialog"
    :property="editingProperty"
    :companies="companies"
    :saving="saving"
    @save="handleSaveProperty"
  />
</template>

<style scoped>
/* Minimal custom styles - using Ant Design utilities where possible */
</style>
